import { toast } from "@birdwatch/ui/toast";
import { useToggle } from "@uidotdev/usehooks";
import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import {
  BwCheckmarkLine,
  BwCircleMessageVoiceLine,
  BwCloseLine,
  BwLoadingLine,
  BwMicrophoneLine,
  BwPauseLine,
} from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { hasBytesLanded, useMediaUpload } from "@/features/shared/media-upload";
import type { AudioAttachment } from "../_types/audio-attachment";
import {
  AudioRecorder,
  AudioRecorderCancel,
  AudioRecorderDuration,
  AudioRecorderSave,
  AudioRecorderTogglePlay,
  AudioRecorderWave,
} from "./audio-recorder";

type AudioRecorderPopoverProps = {
  onChange?: (value: AudioAttachment | null) => void;
  ref?: Ref<{
    reset: () => void;
  }>;
};

export function AudioRecorderPopover({
  onChange,
  ref,
}: AudioRecorderPopoverProps) {
  const { jobs, upload, cancel } = useMediaUpload();
  const [isOpen, setIsOpen] = useToggle(false);
  const [audioAttachment, setAudioAttachment] =
    useState<AudioAttachment | null>(null);
  const prevUrlRef = useRef<string | null>(null);
  /** The media-v3 job carrying the current recording, bound by id. */
  const jobIdRef = useRef<string | null>(null);
  /** True while the mic is open. A ref, not state: the `[jobs]` effect reads it. */
  const isRecordingRef = useRef(false);

  useImperativeHandle(ref, () => ({
    reset() {
      // A recording still on its way up is bytes nobody will send — stop it.
      if (jobIdRef.current) cancel(jobIdRef.current);
      jobIdRef.current = null;
      setAudioAttachment(null);
      prevUrlRef.current = null;
      onChange?.(null);
    },
  }));

  useEffect(() => {
    if (audioAttachment && audioAttachment.status === "done") {
      onChange?.(audioAttachment);
    }
  }, [audioAttachment]);

  // The recording's row follows its upload job: progress while the parts go up,
  // `done` the moment the bytes land (the mp3 re-encode and the transcript are
  // background work the composer never waits on), `error` if the upload failed.
  // The popover stays open — its save button spinning — until one of those two
  // outcomes, so the person always sees the clip being sent up; closing on the
  // save click left a blank composer for the whole upload.
  useEffect(() => {
    const jobId = jobIdRef.current;
    if (!jobId) return;
    const job = jobs.find((candidate) => candidate.id === jobId);
    if (!job) return;
    // This clip's outcome is news exactly ONCE. Media patches the same job again
    // seconds later (`attachment.processed`, then `transcript.ready`) and every
    // patch re-runs this effect — a still-tracked job would close the popover a
    // second time, on top of whatever recording is running by then, ending it
    // mid-clip and attaching the truncated audio.
    if (job.phase === "failed") {
      jobIdRef.current = null;
      if (!isRecordingRef.current) setIsOpen(false);
      if (job.error !== "Upload cancelled") {
        toast.error({
          title: "Couldn't upload the voice message",
          description: job.error ?? undefined,
        });
      }
    } else if (hasBytesLanded(job)) {
      jobIdRef.current = null;
      if (!isRecordingRef.current) setIsOpen(false);
    }
    setAudioAttachment((prev) => {
      if (!prev) return prev;
      const id = job.attachmentId ?? prev.id;
      if (job.phase === "failed") {
        return prev.status === "error"
          ? prev
          : { ...prev, id, status: "error", percent: 0 };
      }
      if (hasBytesLanded(job)) {
        if (prev.status === "done") return prev;
        return {
          ...prev,
          id,
          originalContentUri: job.contentUri ?? prev.originalContentUri,
          status: "done",
          percent: 100,
        };
      }
      const percent =
        job.phase === "finalizing"
          ? 95
          : job.size > 0
            ? Math.min(90, Math.round((job.bytesSent / job.size) * 90))
            : 0;
      if (
        prev.status === "uploading" &&
        prev.percent === percent &&
        prev.id === id
      ) {
        return prev;
      }
      return { ...prev, id, status: "uploading", percent };
    });
  }, [jobs]);

  const handleRecordEnd = useCallback(
    async ({ blob, url }: { blob: Blob; url: string }) => {
      if (prevUrlRef.current === url) {
        return;
      }
      prevUrlRef.current = url;
      const inferredType = blob.type || "audio/webm";
      const mimeBase = inferredType.split(";")[0];
      const extension = mimeBase.split("/")[1] || "webm";
      const fileName = `recording-${Date.now()}.${extension}`;
      const file = new File([blob], fileName, { type: inferredType });
      // A previous recording still uploading is superseded by this one.
      if (jobIdRef.current) cancel(jobIdRef.current);
      const [jobId] = upload([file]);
      jobIdRef.current = jobId ?? null;
      // Mark as uploading immediately; the job effect above takes it from here
      // (and closes the popover once the bytes land or the upload fails).
      setAudioAttachment({
        id: uuidv4(),
        file,
        status: "uploading",
        percent: 0,
        localUrl: url,
      });
    },
    [upload, cancel],
  );

  const handleRecordingChange = useCallback((recording: boolean) => {
    isRecordingRef.current = recording;
  }, []);

  const isUploading = audioAttachment?.status === "uploading";

  // While the clip is going up the popover IS the progress indicator — an
  // outside click must not dismiss it; the job effect closes it on landing.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isUploading) return;
      setIsOpen(open);
    },
    [isUploading, setIsOpen],
  );

  // The X discards the clip — mid-upload too: nobody will send bytes the person
  // just dismissed, and a half-uploaded row would pin the send button.
  const handleCancel = useCallback(() => {
    if (isUploading) {
      if (jobIdRef.current) cancel(jobIdRef.current);
      jobIdRef.current = null;
      setAudioAttachment(null);
      prevUrlRef.current = null;
    }
    setIsOpen(false);
  }, [isUploading, cancel, setIsOpen]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="neutral" kind="ghost" size="icon-md">
              <BwCircleMessageVoiceLine />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Record audio clip</TooltipContent>
      </Tooltip>

      <PopoverContent className="p-2 w-80 rounded-2xl" align="center">
        <AudioRecorder
          autoStart
          stopSignal={!isOpen}
          onRecordEnd={handleRecordEnd}
          onRecordingChange={handleRecordingChange}
        >
          <div className="flex items-center justify-center gap-1">
            <BwMicrophoneLine className="text-text-dark-low" />
            <p className="text-text-dark-low text-xs">
              Recording an audio clip...
            </p>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <AudioRecorderCancel size="icon-md" onCancel={handleCancel}>
              <BwCloseLine />
            </AudioRecorderCancel>
            <AudioRecorderWave />
            <AudioRecorderDuration />
            <AudioRecorderTogglePlay
              className="group"
              size="icon-md"
              variant="neutral"
              kind="ghost"
            >
              <BwMicrophoneLine className="text-text-dark-low group-data-[state=recording]:hidden" />
              <BwPauseLine className="text-text-dark-low group-data-[state=recording]:block hidden" />
            </AudioRecorderTogglePlay>
            <AudioRecorderSave
              size="icon-md"
              disabled={audioAttachment?.status === "uploading"}
            >
              {audioAttachment?.status === "uploading" ? (
                <BwLoadingLine className="animate-spin size-4" />
              ) : (
                <BwCheckmarkLine className="size-4" />
              )}
            </AudioRecorderSave>
          </div>
        </AudioRecorder>
      </PopoverContent>
    </Popover>
  );
}
